// Ocupación de unidades físicas de recursos (fase 3 del rumbo ERP).
//
// Espejo de `components/calendario/clashes.js` para eventos, pero más
// simple: una unidad física no se comparte ni se contiene en otra, así que
// no hay footprints ni incompatibilidades — cualquier cruce temporal es
// conflicto, punto.
//
// La detección es un scan en memoria sobre los eventos que VenueProvider ya
// tiene suscritos. Por eso `recursosAsignados` vive DENORMALIZADO en el doc
// del evento (igual que `proveedoresAsignados`): si las asignaciones
// colgaran del recurso, saber si "Calentador 03" está libre el sábado
// costaría una query por recurso.

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

export const unidadesDeEvento = (evento) => {
  const asignados = Array.isArray(evento?.recursosAsignados)
    ? evento.recursosAsignados
    : [];
  const ids = [];
  asignados.forEach((a) => {
    (Array.isArray(a?.unidadIds) ? a.unidadIds : []).forEach((id) => {
      if (id) ids.push(id);
    });
  });
  return ids;
};

// Devuelve [{ unidadId, evento }] por cada unidad pedida que ya esté tomada
// por otro evento que se traslapa en fechas. Array vacío = todas libres.
export function getUnidadesOcupadas({
  start,
  end,
  unidadIds = [],
  excludeEventId = null,
  events = [],
}) {
  const inicio = toDate(start);
  const fin = toDate(end);
  const buscadas = new Set((unidadIds || []).filter(Boolean));
  if (!inicio || !fin || buscadas.size === 0) return [];

  const conflictos = [];
  (events || []).forEach((ev) => {
    if (!ev) return;
    if (excludeEventId && ev.id === excludeEventId) return;
    // Un evento cancelado libera sus unidades.
    if (ev.status === "cancelado") return;
    const evInicio = toDate(ev.startTime);
    const evFin = toDate(ev.endTime) || evInicio;
    if (!evInicio || !evFin) return;
    if (!(inicio < evFin && fin > evInicio)) return;
    unidadesDeEvento(ev).forEach((unidadId) => {
      if (buscadas.has(unidadId)) conflictos.push({ unidadId, evento: ev });
    });
  });
  return conflictos;
}

// Índice unidadId -> evento que la tiene, para pintar el picker sin correr
// la detección una vez por unidad.
export function ocupacionPorUnidad({ start, end, excludeEventId, events, unidadIds }) {
  const conflictos = getUnidadesOcupadas({
    start,
    end,
    unidadIds,
    excludeEventId,
    events,
  });
  const map = {};
  conflictos.forEach(({ unidadId, evento }) => {
    if (!map[unidadId]) map[unidadId] = evento;
  });
  return map;
}

// Eventos futuros que ya tienen asignada una unidad. Se usa al mandarla a
// mantenimiento: no se bloquea el cambio de estado (el owner sabe algo que
// el sistema no), pero sí se le dice a qué eventos les va a faltar.
export function eventosFuturosConUnidad({ unidadId, events = [], ahora = new Date() }) {
  if (!unidadId) return [];
  return (events || [])
    .filter((ev) => ev && ev.status !== "cancelado")
    .filter((ev) => unidadesDeEvento(ev).includes(unidadId))
    .filter((ev) => {
      const inicio = toDate(ev.startTime);
      return inicio && inicio >= ahora;
    })
    .sort((a, b) => toDate(a.startTime) - toDate(b.startTime));
}

// ── Reservas en la pieza ───────────────────────────────────────────────
//
// El scan en memoria de arriba depende de que `events` esté al día, y una
// suscripción puede ir segundos atrás de otro owner. Por eso la verdad del
// bloqueo vive TAMBIÉN en la pieza: `unidades/{id}.reservas` es la lista de
// [{ eventId, eventoNombre, start, end }] que la tienen tomada, y se escribe
// en la misma transacción que `recursosAsignados` (ver utils/inventario.js).
// Dos asignaciones simultáneas de la misma pieza chocan en Firestore: una
// gana, la otra reintenta, lee la reserva nueva y devuelve el conflicto.
//
// Las piezas dadas de alta antes de esto no tienen `reservas`; el scan en
// memoria sigue cubriendo esos eventos viejos.

export const solapan = (aInicio, aFin, bInicio, bFin) => {
  const ai = toDate(aInicio);
  const af = toDate(aFin) || ai;
  const bi = toDate(bInicio);
  const bf = toDate(bFin) || bi;
  if (!ai || !af || !bi || !bf) return false;
  return ai < bf && af > bi;
};

// Reservas de OTROS eventos que se cruzan con [start, end]. Devuelve las
// entradas tal cual (con eventId y eventoNombre) para poder decir quién.
export function reservasEnConflicto({
  reservas = [],
  start,
  end,
  excludeEventId = null,
}) {
  return (Array.isArray(reservas) ? reservas : []).filter((r) => {
    if (!r?.eventId) return false;
    if (excludeEventId && r.eventId === excludeEventId) return false;
    return solapan(start, end || start, r.start, r.end);
  });
}

// ── Piezas por cantidad (pool) ─────────────────────────────────────────
//
// En el modo por cantidad no hay pieza que reservar, hay un cupo. La verdad del
// bloqueo vive en UN doc —`conceptos/{id}/reservas/pool`, con la lista de
// [{eventId, eventoNombre, cantidad, start, end}]— porque un solo doc es lo
// que hace que Firestore pueda serializar a dos owners que apartan sillas al
// mismo tiempo: los dos lo leen en su transacción, uno gana, el otro reintenta,
// ve la reserva nueva y se lleva el conflicto ("quedan 20 y pediste 100").
//
// Va en subcolección y no en el doc del servicio a propósito: el catálogo se
// suscribe completo en todo el app y no tiene por qué cargar con el historial
// de reservas de cada servicio.
//
// El scan en memoria sobre `recursosAsignados` se queda como respaldo de los
// eventos anteriores al doc, igual que con las piezas identificadas. Se unen
// POR eventId para no contar dos veces al mismo evento.

// Cuántas piezas de este servicio tiene apartadas un evento.
export const cantidadDeEvento = (evento, conceptoId) => {
  const asignados = Array.isArray(evento?.recursosAsignados) ? evento.recursosAsignados : [];
  const entrada = asignados.find((a) => a?.recursoId === conceptoId);
  return Math.max(0, Math.floor(Number(entrada?.cantidad) || 0));
};

/**
 * Cuántas piezas están comprometidas en [start, end] por OTROS eventos, y por
 * quiénes. `reservas` es la lista del doc del pool (la verdad fresca) y
 * `events` el respaldo para los eventos que no están en ella.
 */
export function ocupacionPool({
  reservas = [],
  events = [],
  conceptoId,
  start,
  end,
  excludeEventId = null,
}) {
  const porEvento = new Map();
  reservasEnConflicto({ reservas, start, end, excludeEventId }).forEach((r) => {
    const cantidad = Math.max(0, Math.floor(Number(r.cantidad) || 0));
    if (cantidad > 0) {
      porEvento.set(r.eventId, {
        eventId: r.eventId,
        nombre: r.eventoNombre || "",
        cantidad,
      });
    }
  });

  (events || []).forEach((ev) => {
    if (!ev?.id || ev.status === "cancelado") return;
    if (excludeEventId && ev.id === excludeEventId) return;
    // Ya lo dijo el doc del pool: su cifra manda, es la fresca.
    if (porEvento.has(ev.id)) return;
    if (!solapan(start, end || start, ev.startTime, ev.endTime || ev.startTime)) return;
    const cantidad = cantidadDeEvento(ev, conceptoId);
    if (cantidad > 0) {
      porEvento.set(ev.id, { eventId: ev.id, nombre: ev.nombre || "", cantidad });
    }
  });

  const eventos = [...porEvento.values()].sort((a, b) => b.cantidad - a.cantidad);
  return { ocupadas: eventos.reduce((s, e) => s + e.cantidad, 0), eventos };
}

/** Disponibilidad del pool en una fecha: reservables − ocupadas. */
export function disponibilidadPool({
  reservables = 0,
  reservas = [],
  events = [],
  conceptoId,
  start,
  end,
  excludeEventId = null,
}) {
  const { ocupadas, eventos } = ocupacionPool({
    reservas,
    events,
    conceptoId,
    start,
    end,
    excludeEventId,
  });
  const total = Math.max(0, Math.floor(Number(reservables) || 0));
  return {
    reservables: total,
    ocupadas,
    disponibles: Math.max(0, total - ocupadas),
    // Más comprometido que existente: pasa si el owner bajó el total o mandó
    // piezas a mantenimiento después de apartarlas. Se enseña, no se corrige
    // solo: quitarle piezas a un evento por su cuenta es peor.
    sobrevendido: Math.max(0, ocupadas - total),
    eventos,
  };
}
